import { motion } from "framer-motion";
import { Mail, Phone } from "lucide-react";
import { useLanguage } from "@/i18n";

const HablemosPage = () => {
  const { t } = useLanguage();
  
  // Image URLs
  const leftImage = "https://customer-assets.emergentagent.com/job_6755e027-e15f-4d08-a33d-914c47bd2f3e/artifacts/e4mquw57_Roger_Delmont_upscale_upscaled%202.jpg";
  const rightImage = "https://customer-assets.emergentagent.com/job_6755e027-e15f-4d08-a33d-914c47bd2f3e/artifacts/wvtgep6u_Roger_Delmont_upscale_upscaled.jpg";

  return (
    <div className="min-h-screen pt-20 lg:pt-24" data-testid="hablemos-page">
      {/* Film grain overlay */}
      <div className="fixed inset-0 pointer-events-none grain-overlay opacity-[0.025] z-0" />

      {/* Hero Section with Title and Intro Text */}
      <section className="relative py-16 lg:py-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
          >
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-wide text-foreground mb-6">
              {t.hablemos.pageTitle}
            </h1>
            <div className="w-16 h-px bg-primary/40 mb-8" />
            <p className="text-lg lg:text-xl text-foreground/70 leading-relaxed font-light max-w-2xl" data-testid="intro-text">
              {t.hablemos.introText}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Central Image + Text Layout (3 columns) */}
      <section className="relative py-12 lg:py-16">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 items-center"
            data-testid="central-layout"
          >
            {/* Left Image */}
            <div className="flex justify-center">
              <img
                src={leftImage}
                alt="Spurna Creativa"
                className="w-full max-w-[280px] h-auto object-cover rounded-sm"
                data-testid="left-image"
              />
            </div>

            {/* Center Text */}
            <div className="flex items-center justify-center text-center py-8 md:py-0">
              <p className="font-serif text-2xl lg:text-3xl text-foreground/90 leading-relaxed font-light" data-testid="central-text">
                {t.hablemos.centralText}
              </p>
            </div>

            {/* Right Image */}
            <div className="flex justify-center">
              <img
                src={rightImage}
                alt="Spurna Creativa"
                className="w-full max-w-[280px] h-auto object-cover rounded-sm"
                data-testid="right-image"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Block */}
      <section className="relative py-12 lg:py-16">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.4, 0, 0.2, 1] }}
            className="text-center"
            data-testid="contact-block"
          >
            <h3 className="font-serif text-2xl lg:text-3xl text-foreground mb-8">
              {t.hablemos.contactTitle}
            </h3>
            
            {/* Email */}
            <div className="flex flex-col items-center gap-3 mb-6">
              <div className="flex items-center gap-3 text-foreground/80">
                <Mail className="w-5 h-5 text-primary/60" />
                <a href="mailto:emma@spurnacreativa.com" className="text-base lg:text-lg hover:text-foreground transition-colors" data-testid="email-emma">
                  emma@spurnacreativa.com
                </a>
              </div>
              <div className="flex items-center gap-3 text-foreground/80">
                <Mail className="w-5 h-5 text-primary/60" />
                <a href="mailto:roger@spurnacreativa.com" className="text-base lg:text-lg hover:text-foreground transition-colors" data-testid="email-roger">
                  roger@spurnacreativa.com
                </a>
              </div>
            </div>

            {/* Phone */}
            <div className="flex items-center justify-center gap-3 text-foreground/80">
              <Phone className="w-5 h-5 text-primary/60" />
              <span className="text-base lg:text-lg" data-testid="phone-numbers">
                +34 617 48 10 95 / +34 650 49 23 68
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Thoreau Quote */}
      <section className="relative py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: [0.4, 0, 0.2, 1] }}
            className="text-center"
            data-testid="thoreau-quote"
          >
            <blockquote className="font-serif text-xl lg:text-2xl text-foreground/60 italic leading-relaxed">
              &ldquo;{t.hablemos.quote}&rdquo;
            </blockquote>
            <p className="mt-4 text-base text-foreground/50 font-light">
              {t.hablemos.quoteAuthor}
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HablemosPage;
